import { useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { EditIcon, FileDownloadIcon } from "@dotnet/telematics-design-system";

import TctWebIcon from "assets/customIcons/navigation/TctWebIcon";
import AuthContext from "context/auth/authContext";
import {
    downloadFile,
    FILE_TYPES,
    getEnvironment,
    isGlobalFile,
    isInternalOrPremiumLevel,
    RenderConditionOptions,
    useApi,
    usePermissions,
    useTct
} from "shared";

import { TITLES } from "../constants";

const useFileInlineActions = () => {
    const { user } = useContext(AuthContext);

    const { t } = useTranslation();
    const { getData, handleResponse } = useApi();
    const { editConfigurationOnline } = useTct();

    const {
        isDownloadFileNotAllowed,
        isEditInTctWebNotAllowed,
        PERMISSIONS,
        getNoPermissionTooltipTitle
    } = usePermissions();

    const { isFotaGlobalEnv } = getEnvironment();

    const [rowData, setRowData] = useState(null);
    const [isOpen, setIsOpen] = useState(false);

    const openDialog = (data: any) => {
        setRowData(data);
        setIsOpen(true);
    };

    const closeDialog = () => {
        setRowData(null);
        setIsOpen(false);
    };

    const canEdit = (type: FILE_TYPES) => {
        switch (type) {
            case FILE_TYPES.AdasFw:
            case FILE_TYPES.ApxFw:
            case FILE_TYPES.BlackBox:
            case FILE_TYPES.BleFw:
            case FILE_TYPES.BlueNrg:
            case FILE_TYPES.BluetoothAuthorizationList:
            case FILE_TYPES.CanChipFw:
            case FILE_TYPES.CanDebugLog:
            case FILE_TYPES.CarrierApxFw:
            case FILE_TYPES.CarrierApxSettings:
            case FILE_TYPES.CarrierAdvanceFw:
            case FILE_TYPES.CarrierAdvanceSettings:
            case FILE_TYPES.CarrierExtractions:
            case FILE_TYPES.CarrierLoggerFiles:
            case FILE_TYPES.Certificate:
            case FILE_TYPES.Configuration:
            case FILE_TYPES.CrashDump:
            case FILE_TYPES.DeviceBulkAttributeUpdate:
            case FILE_TYPES.DeviceExportFileCsv:
            case FILE_TYPES.DeviceExportFileXlsx:
            case FILE_TYPES.DeviceImportFile:
            case FILE_TYPES.DsmFw:
            case FILE_TYPES.Dtb:
            case FILE_TYPES.ExceptionInfo:
            case FILE_TYPES.Firmware:
            case FILE_TYPES.FmObdChipFw:
            case FILE_TYPES.Generic:
            case FILE_TYPES.GnssmDownloadAssistant:
            case FILE_TYPES.GnssmFw:
            case FILE_TYPES.IdCollection:
            case FILE_TYPES.Log:
            case FILE_TYPES.ManualGeofenceList:
            case FILE_TYPES.MeiGModemFw:
            case FILE_TYPES.NetprismaModemFirmware:
            case FILE_TYPES.NmeaLog:
            case FILE_TYPES.OneWireAuthorizationList:
            case FILE_TYPES.Psub:
            case FILE_TYPES.QuectelModemFw:
            case FILE_TYPES.SimcomModemFirmware:
            case FILE_TYPES.WdLog:
                return true;
            default:
                return false;
        }
    };

    const getDisabledEditTctWebText = () => {
        if (isEditInTctWebNotAllowed || isDownloadFileNotAllowed) {
            return getNoPermissionTooltipTitle([
                PERMISSIONS.FileTctWebAccess,
                PERMISSIONS.FileDownload
            ]);
        }

        if (!isInternalOrPremiumLevel(user?.company_level)) {
            return t(`General##edit in tct web disabled`);
        }

        return undefined;
    };

    const inlineActions = [
        {
            title: TITLES.EditInTctWeb,
            icon: TctWebIcon,
            getDisabledText: () => getDisabledEditTctWebText(),
            clickAction: (data: any) => editConfigurationOnline(data?.id),
            renderConditions: ({ data }: RenderConditionOptions) =>
                isFotaGlobalEnv && data.web_editor_support
        },
        {
            title: TITLES.DownloadFile,
            hasLoader: true,
            icon: FileDownloadIcon,
            getDisabledText: () =>
                getNoPermissionTooltipTitle(PERMISSIONS.FileDownload),
            clickAction: (data: any, removeLoading: () => void) =>
                downloadFile({
                    id: data.id,
                    getData,
                    handleResponse,
                    removeLoading
                }),
            renderConditions: ({ data }: RenderConditionOptions) =>
                Boolean(data?.is_downloadable)
        },
        {
            title: TITLES.EditFile,
            icon: EditIcon,
            getDisabledText: () =>
                getNoPermissionTooltipTitle(PERMISSIONS.FileUpdate),
            clickAction: (data: any) => openDialog(data),
            renderConditions: ({ data }: RenderConditionOptions) =>
                !isGlobalFile(data.company_id) && canEdit(data.type)
        }
    ];

    return {
        inlineActions,
        rowData,
        isOpen,
        closeDialog
    };
};

export { useFileInlineActions };
